<template>
  <div class="flex flex-col w-full h-full bg-white">
    <BaseTableControls
      v-model="searchQuery"
      is-saving-layout-hidden
      :is-add-new-hidden="mode == 'view'"
      is-filter-button-hidden
      @add-new="emit('addNew')"
      @refresh-data="handleRefreshData(true)"
      @update-search="onSearchUpdate"
      @export-to-excel="onExportToExcel" />
    <BaseTableMultiSelect
      v-if="multiSelectedItems.length"
      @close="multiSelectedItems = []"
      hide-download-button
      :hide-define-set-button="false"
      :hide-move-to-button="hideMultiselectMoveTo"
      @define-set="onDefineSet"
      @move="onMoveTo"
      @deleteMultiselect="modalMultiDeleteConfirmation = true"
      :selectedLength="multiSelectedItems.length"></BaseTableMultiSelect>
    <div class="flex-grow overflow-y-auto rounded">
      <table class="w-full">
        <thead
          class="sticky top-0 z-10 text-xs font-normal text-left text-gray-500 uppercase bg-gray-50">
          <template v-for="(header, index) in headers" :key="header.value">
            <th
              :data-value="header.value"
              scope="col"
              class="px-3 py-2 text-xs font-normal text-left text-gray-400 uppercase border border-b-0 cursor-pointer">
              <div class="flex items-center gap-2">
                <BaseCheckbox
                  v-if="header.value == 'mustHave'"
                  v-model="selectBoxValue"
                  :disabled="mode == 'view'"
                  :icon="selectIcon"
                  @click="onSelectAllClick" />
                {{ header.text }}
                <span @click="handleSort(index)" v-if="header.value !== 'actions'">
                  <Icon icon="fluent:arrow-sort-20-regular" v-if="sortColumn != index" />
                  <Icon
                    icon="fluent:arrow-sort-up-20-regular"
                    v-if="sortColumn == index && sortOrder == 'asc'" />
                  <Icon
                    icon="fluent:arrow-sort-down-20-regular"
                    v-if="sortColumn == index && sortOrder == 'desc'" />
                </span>
              </div>
            </th>
          </template>
        </thead>
        <tbody class="bg-white border divide-y divide-gray-200">
          <tr
            v-for="(item, index) in list"
            :key="item.id"
            class="select-none hover:bg-gray-50"
            @mouseenter="hoveredRow = index"
            @mouseleave="hoveredRow = -1"
            @click.ctrl="multiSelect(item)"
            @click.shift.prevent="multiSelect(item)"
            :class="{
              'bg-theme-50 hover:bg-theme-100': isSelected(item),
            }">
            <template v-for="header in headers" :key="header.value">
              <template v-if="header.active && header.value != 'actions'">
                <td
                  :key="header.value"
                  class="py-1.5 border"
                  :class="
                    header.type == 'check' || header.type === 'status'
                      ? 'pl-3 border-r-transparent w-4'
                      : ''
                  ">
                  <BaseCheckbox
                    v-if="header.value == 'mustHave'"
                    :disabled="mode == 'view'"
                    class="w-fit"
                    v-model="item.mustHave"
                    @click.exact="onItemCheckBoxClick(item.id)" />
                  <div v-else class="flex items-center">
                    <BaseTableCellText
                      :item="getItemValue(item, header.value)"
                      :header="header.value"
                      :searchQuery="searchQuery"></BaseTableCellText>
                    <button
                      v-if="mode == 'edit'"
                      type="button"
                      class="ml-auto mr-4 border-0"
                      :class="{ 'hidden': hoveredRow != index }"
                      @click.exact="emit('delete', item)">
                      <Icon icon="tdesign:delete-1" class="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                </td>
              </template>
              <template v-else-if="header.value === 'actions'">
                <td class="py-1.5 whitespace-nowrap">
                  <slot name="actions" :item="item"></slot>
                </td>
              </template>
            </template>
          </tr>
        </tbody>
      </table>
      <BaseEmptyStateAdd v-if="list.length <= 0" class="border" @add-new="emit('addNew')" />
    </div>
    <BaseModalConfirm
      v-if="modalMultiDeleteConfirmation"
      @cancel="modalMultiDeleteConfirmation = false"
      @confirm="onDeleteMulti" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import BaseTableMultiSelect from '../common/BaseTableMultiSelect.vue';
import BaseTableControls from '@/components/common/BaseTableControls.vue';
import BaseCheckbox from '../common/BaseCheckbox.vue';
import BaseTableCellText from '../common/BaseTableCellText.vue';
import { Icon } from '@iconify/vue';
import { excelParser } from '@/utils/excel-parser';
import BaseEmptyStateAdd from '../common/BaseEmptyStateAdd.vue';
interface Header {
  value: string;
  text: string;
  type?: string;
  active: boolean;
}

interface Item<T = any> {
  [key: string]: T;
}

const props = defineProps<{
  headers: Header[];
  list: Item<any>[];
  tableId: string;
  mode: 'edit' | 'view';
  hideMultiselectMoveTo?: boolean;
}>();

const emit = defineEmits([
  'refreshData',
  'applyFilters',
  'resetFilters',
  'deleteMultiselect',
  'addNew',
  'requiredStatusChange',
  'selectAll',
  'delete',
  'defineSet',
  'moveTo',
]);

const searchQuery = ref<string>('');

function onSearchUpdate() {
  handleRefreshData();
}

const multiSelectedItems = ref<any[]>([]);

function isSelected(item: any) {
  return multiSelectedItems.value.find((multiSelectedItem) => multiSelectedItem.id === item.id);
}

function multiSelect(item: any) {
  if (props.mode == 'view') return;
  if (!isSelected(item)) {
    multiSelectedItems.value.push(item);
  } else {
    multiSelectedItems.value = multiSelectedItems.value.filter((value) => {
      return value.id != item.id;
    });
  }
}

function getItemValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

const sortColumn = ref<number>();
const sortOrder = ref<'asc' | 'desc'>('asc');

function handleSort(index: number) {
  if (sortColumn.value == index) {
    sortOrder.value = sortOrder.value == 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = index;
    sortOrder.value = 'asc';
  }
  handleRefreshData();
}

const modalMultiDeleteConfirmation = ref(false);

function onDeleteMulti() {
  modalMultiDeleteConfirmation.value = false;
  emit('deleteMultiselect', multiSelectedItems.value);
  multiSelectedItems.value = [];
}

function handleRefreshData(reset?: boolean) {
  if (reset) searchQuery.value = '';
  emit('refreshData', {
    column: sortColumn.value,
    order: sortOrder.value,
    query: searchQuery.value,
  });
}

const selectBoxValue = computed(() => props.list.filter((x) => x.mustHave).length > 0);

const selectIcon = computed(() => {
  if (props.list.filter((x) => x.mustHave).length == props.list.length) return 'check';
  return 'dash';
});

function onItemCheckBoxClick(itemId: string) {
  if (props.mode == 'edit') emit('requiredStatusChange', itemId);
}

function onSelectAllClick() {
  if (props.mode == 'view') return;
  const itemsChecked = props.list.filter((x) => x.mustHave).length;
  if (itemsChecked > 0) emit('selectAll', false);
  else if (itemsChecked == 0) emit('selectAll', true);
}

const hoveredRow = ref(-1);

function onExportToExcel() {
  try {
    if (multiSelectedItems.value.length) {
      excelParser({
        data: multiSelectedItems.value,
        headers: props.headers,
        tableId: props.tableId,
      });
    } else {
      excelParser({ data: props.list, headers: props.headers, tableId: props.tableId });
    }
  } catch (error) {
    console.log('there was an error', error);
  }
}

function onDefineSet() {
  emit('defineSet', multiSelectedItems.value);
  multiSelectedItems.value = [];
}

function onMoveTo() {
  emit('moveTo', multiSelectedItems.value);
  multiSelectedItems.value = [];
}
</script>
